import React, { useState, useCallback, useEffect } from 'react';
import { orderApi } from '../../lib/orderApi';
import { goldButton } from '../PageShell';
import { ProductRow } from './products/ProductRow';
import { NewProductForm } from './products/NewProductForm';
import { cfgInput } from './products/productUtils';

const AdminProducts = ({ isOpen }) => {
  const [products, setProducts] = useState([]);
  const [prodMsg, setProdMsg] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [orderDisc, setOrderDisc] = useState('');

  const loadProducts = useCallback(async () => {
    try {
      const { data } = await orderApi.get('/admin/products');
      setProducts(data.products.map((p) => ({ ...p, _tax_pct: Math.round((p.tax_rate || 0) * 1000) / 10 })));
    } catch {
      setProdMsg('Error al cargar productos');
    }
  }, []);

  useEffect(() => {
    if (isOpen) loadProducts();
    if (isOpen) {
      orderApi.get('/admin/settings')
        .then(({ data }) => setOrderDisc(String(data.settings?.order_discount_pct ?? 0)))
        .catch(() => {});
    }
  }, [isOpen, loadProducts]);

  const saveOrderDiscount = async () => {
    const v = Number(orderDisc);
    if (isNaN(v) || v < 0 || v > 90) { setProdMsg('El descuento de orden debe estar entre 0 y 90%.'); return; }
    setProdMsg('');
    try {
      await orderApi.put('/admin/settings', { order_discount_pct: v });
      setProdMsg(v > 0 ? `✓ Descuento del ${v}% activo en todas las órdenes.` : '✓ Descuento de orden desactivado.');
    } catch (e) {
      setProdMsg('Error: ' + (e.response?.data?.detail || ''));
    }
  };

  const setProdField = (id, field, value) => setProducts((prev) => prev.map((p) => (p.id === id ? { ...p, [field]: value } : p)));

  const saveProduct = async (p) => {
    setProdMsg('');
    if (p.price === '' || isNaN(Number(p.price)) || Number(p.price) < 0) {
      setProdMsg('Ingresa un precio válido antes de guardar.');
      return;
    }
    try {
      await orderApi.put(`/admin/products/${p.id}`, { price: Number(p.price), tax_rate: Number(p._tax_pct) / 100 });
      setProdMsg(`✓ ${p.name.es} actualizado.`);
      loadProducts();
    } catch (e) {
      setProdMsg('Error: ' + (e.response?.data?.detail || ''));
    }
  };

  const moveProduct = async (p, direction) => {
    setProdMsg('');
    try {
      await orderApi.post(`/admin/products/${p.id}/move`, { direction });
      loadProducts();
    } catch (e) {
      setProdMsg('Error al mover: ' + (e.response?.data?.detail || ''));
    }
  };

  const saveEdit = async (payload, name) => {
    setProdMsg('');
    try {
      await orderApi.put(`/admin/products/${editingId}`, payload);
      setProdMsg(`✓ ${name} actualizado.`);
      setEditingId(null);
      loadProducts();
    } catch (e) {
      setProdMsg('Error: ' + (e.response?.data?.detail || ''));
    }
  };

  const deleteProduct = async (p) => {
    if (!window.confirm(`¿Eliminar "${p.name.es}"?`)) return;
    try {
      await orderApi.delete(`/admin/products/${p.id}`);
      setProdMsg(`✓ ${p.name.es} eliminado.`);
      loadProducts();
    } catch (e) {
      setProdMsg('Error: ' + (e.response?.data?.detail || ''));
    }
  };

  if (!isOpen) return null;

  return (
    <div className="mt-5 p-5" style={{ background: '#141414', border: '1px solid rgba(201,168,76,0.3)' }} data-testid="products-panel">
      <h3 className="font-display" style={{ fontSize: '18px', fontWeight: 700, color: '#F5F5F0' }}>Productos (menú de delivery)</h3>
      <p className="font-body mt-1" style={{ fontSize: '12px', color: '#C2C1B6' }}>
        Usa las flechas para cambiar el orden en que los clientes ven el menú. "Editar" abre nombre, descripción, categoría, imagen, tags y disponibilidad.
      </p>
      {prodMsg && (
        <p className="font-body mt-2" style={{ fontSize: '12px', color: prodMsg.startsWith('✓') ? '#7FB77E' : '#E0736F' }} data-testid="products-msg">
          {prodMsg}
        </p>
      )}

      <div className="mt-3 space-y-2">
        {products.map((p, i) => (
          <ProductRow
            key={p.id}
            p={p}
            index={i}
            count={products.length}
            editing={editingId === p.id}
            onToggleEdit={() => setEditingId(editingId === p.id ? null : p.id)}
            onMove={moveProduct}
            onFieldChange={setProdField}
            onSaveInline={saveProduct}
            onSaveEdit={saveEdit}
            onDelete={deleteProduct}
            setMsg={setProdMsg}
          />
        ))}
      </div>

      <div className="mt-5 p-4" style={{ background: '#0A0A0A', border: '1px dashed rgba(201,168,76,0.3)' }}>
        <p className="font-body uppercase" style={{ fontSize: '10px', letterSpacing: '2px', color: '#C9A84C' }}>Descuento a toda la orden</p>
        <p className="font-body mt-1" style={{ fontSize: '11px', color: '#C2C1B6' }}>
          Este porcentaje se resta del subtotal de cada pedido nuevo (0 = desactivado). Los clientes lo verán como línea de descuento en su carrito y recibo.
        </p>
        <div className="flex items-center gap-2 mt-2">
          <input type="number" min="0" max="90" step="0.5" value={orderDisc} onChange={(e) => setOrderDisc(e.target.value)} style={{ ...cfgInput, width: '90px' }} data-testid="order-discount-input" />
          <span className="font-body" style={{ fontSize: '13px', color: '#C2C1B6' }}>%</span>
          <button onClick={saveOrderDiscount} style={{ ...goldButton, padding: '8px 16px' }} data-testid="order-discount-save">Guardar</button>
        </div>
      </div>

      <NewProductForm onCreated={loadProducts} setMsg={setProdMsg} />
    </div>
  );
};

export default AdminProducts;
